import { LitElement, css, html } from "lit";
import { fa, onceParentAvailable } from "../../utils/widgets.js";
import { faDownload, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { SEMANTICS_OVERLAY_MINZOOM } from "../../utils/SemanticsMapProtocol.js";

/**
 * Semantics Filters menu allows user to show/hide specific semantic tags over the map.
 * @class Panoramax.components.menus.SemanticsFilters
 * @element pnx-map-semantics-filters-menu
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @slot `extra` Append custom content at the end of the menu (e.g. integrator-specific filters). Slotted content is light-DOM owned by the host, so it survives the menu's re-renders.
 * @fires Panoramax.components.menus.SemanticsFilters#change
 * @example
 * ```html
 * <pnx-map-semantics-filters-menu _parent=${viewer} />
 * ```
 */
export default class SemanticsFilters extends LitElement {
	/** @private */
	static styles = css`
		.pnx-tags-zoomin {
			z-index: 131;
			background-color: rgba(255,255,255,0.8);
			text-align: center;
			font-weight: 800;
			position: absolute;
			top: 0;
			right: 0;
			left: 0;
			bottom: 0;
			display: flex;
			justify-content: center;
			align-items: center;
			border-radius: 25px;
		}
		.pnx-tags-zoomin.hidden {
			display: none;
		}
		.pnx-tags-cta {
			display: flex;
			align-items: center;
  			justify-content: end;
		}
		pnx-switch {
			display: block;
			margin: 5px 0;
		}
	`;

	/** @private */
	static properties = {
		_showZoomIn: {state: true},
		_layers: {state: true},
	};

	constructor() {
		super();
		this.showZoomIn = true;
		this._layers = [];

		// Map zoom
		onceParentAvailable(this).then(() => this._parent.onceMapReady?.().then(() => {
			const postUpdate = () => {
				this._onLayersChange();
				this.requestUpdate();
			};
			this._parent.map.on("zoomend", this._onMapZoom.bind(this));
			this._parent.map.on("styledata", postUpdate);
			this._parent.mapStyleComposer.addEventListener("dataoverlay-added", postUpdate);
			this._onMapZoom();
			postUpdate();
		}));
	}

	/** @private */
	_layersToSet(layers) {
		return layers
			.map(l => l.source+"/"+this._parent.mapStyleComposer.dataOverlays.has(l.source))
			.sort()
			.join("|");
	}

	/** @private */
	_onLayersChange() {
		clearTimeout(this._debouncedChange);
		this._debouncedChange = setTimeout(
			() => {
				const newlayers = this._parent?.map?.getSemanticOverlays();
				// eslint-disable-next-line eqeqeq
				if(this._layersToSet(newlayers) != this._layersToSet(this._layers)) {
					this._layers = newlayers;

					/**
					 * Event for semantics overlay visibility changes
					 * @event Panoramax.components.menus.SemanticsFilters#change
					 * @type {CustomEvent}
					 * @property {object[]} detail.layers List of semantics layers, from MapLibre
					 */
					this.dispatchEvent(new CustomEvent("change", {
						bubbles: true,
						composed: true,
						detail: { layers: newlayers }
					}));
				}
			},
			10
		);
	}

	/**
	 * Map zoom event handler: show/hide "zoom in" labels
	 * @private
	 */
	_onMapZoom() {
		this._showZoomIn = this._parent.map.getZoom() < SEMANTICS_OVERLAY_MINZOOM;
	}

	/** @private */
	render() {
		if(!this._layers || this._layers.length === 0) { return; }

		return html`
			<div class="pnx-tags-zoomin ${this._showZoomIn ? "" : "hidden"}">
				${this._parent?._t.pnx.semantics_zoom_in}
			</div>

			<div class="pnx-tags-cta">
				<pnx-button
					kind="full"
					size="sm"
					@click=${() => this._parent?._showSemanticsDownload()}
				>${fa(faDownload)} ${this._parent?._t.pnx.semantics_download}</pnx-button>

				<pnx-button
					id="pnx-semfilt-doc"
					kind="superinline"
					size="md"
					title=${this._parent?._t.pnx.semantics_doc}
					@click=${() => this._parent?._showSemanticsDoc()}
				>${fa(faInfoCircle)}</pnx-button>
			</div>

			${this._layers.filter(l => l.metadata).map(l => html`
				<pnx-switch
					name=${"pnx-semfilt-"+l.id}
					.checked=${this._parent.mapStyleComposer.dataOverlays.has(l.source)}
					@change=${e => this._parent.mapStyleComposer.switchDataOverlayVisibility(l.source, e.target.checked)}
				>${this._parent?._t.pnx.semantics_overlays[l.id] || l.metadata.name || l.id}</pnx-switch>
			`)}
			<slot name="extra"></slot>
		`;
	}
}

customElements.define("pnx-map-semantics-filters-menu", SemanticsFilters);
